<template>
  <div class="service-system-form">
    <!-- Service History Section -->
    <b-button
      v-if="isButton"
      variant="primary"
      block
      v-b-modal.create-service-form
      class="btn-search"
    >
      <div class="d-flex align-items-center">
        {{ $t('button.createServiceOrder') }}
        <feather-icon icon="PlusIcon" size="17" class="align-middle text-body btn-add" />
      </div>
    </b-button>
    <section v-else class="service-history mt-0">
      <div class="d-flex justify-content-between align-items-center">
        <p class="section-label mb-0">
          {{ $t('service.service_history') }}
        </p>
        <FeatherIcon
          v-if="$can('manage', 'create-service')"
          v-b-modal.create-service-form
          class="clickable-icon"
          icon="PlusIcon"
          size="24"
        />
        <FeatherIcon
          v-else
          v-b-tooltip.hover
          :title="$t('general.create_service_disabled')"
          class="disabled-icon"
          icon="PlusIcon"
          size="24"
        />
      </div>
    </section>

    <!-- Create Service Modal -->
    <b-modal
      id="create-service-form"
      ref="modal"
      centered
      size="xl"
      :title="$t('button.createServiceOrder')"
      hide-footer
      :no-close-on-backdrop="true"
      @hidden="onHidden"
    >
      <validation-observer
        ref="formValidation"
        #default="{ invalid }"
      >
        <!-- Basic Information Section -->
        <div class="basic-info mb-2">
          <h6 class="mb-1">
            {{ $t('service.requestor') }}
          </h6>
          <div class="info-card border rounded p-2 d-flex gap-2">
            <div class="employee-search">
              <label class="d-block text-muted mb-25">{{ $t('breadcrumb.project') }}</label>
              <n-async-single-select
                v-model="formData.project"
                repository="project"
                selection-key="id"
                selection-label="translations"
                :is-object="true"
                :query="{ isActive: true }"
                :disabled-display-group="true"
                :is-cached="true"
                :is-server-filtered="true"
                :immediate="false"
                :placeholder="$t('placeholder.selectProject')"
                v-bind:tmpList.sync="projectList"
                @change="handleProjectChange"
              />

              <label class="d-block text-muted mt-1 mb-25">{{ $t('field.enterprise') }}</label>
              <n-async-single-select
                v-model="formData.enterprise"
                name="enterprise"
                repository="thirdPartyEnterprise"
                selection-key="id"
                selection-label="translations"
                :is-object="true"
                :is-server-filtered="true"
                :readonly="!formData.project"
                :immediate="false"
                :query="{ projectId: formData.project ? formData.project.id : undefined }"
                :placeholder="$t('placeholder.selectEnterprise')"
                @change="handleEnterpriseChange"
              />

              <label class="d-block text-muted mt-1 mb-25">{{
                $t('breadcrumb.employee')
              }}</label>
              <n-async-single-select
                :key="resetEnterpriseIndex"
                v-model="formData.employee"
                name="employee"
                repository="thirdParty"
                selection-key="id"
                selection-label="employeeName"
                :disabled-display-group="true"
                :query="{ enterpriseId:formData.enterprise ? formData.enterprise.id : undefined }"
                :readonly="!formData.enterprise"
                :suffix-label="$t('field.owner')"
                :auto-select-is-owner="!!formData.enterprise"
                v-bind:tmpList.sync="employeeList"
                :placeholder="$t('placeholder.selectEmployee')"
                :is-cached="true"
                :immediate="!!formData.enterprise"
              />
            </div>
            <div class="d-flex align-items-center details-right">
              <!-- Profile Image -->
              <div class="profile-image-container mr-3">
                <img
                  v-if="visitorMetaDataComputed.profileUrl"
                  :src="visitorMetaDataComputed.profileUrl"
                  :alt="visitorMetaDataComputed.name"
                  class="profile-image"
                >
                <div
                  v-else
                  class="profile-image-placeholder"
                >
                  <FeatherIcon
                    icon="UserIcon"
                    size="48"
                  />
                </div>
              </div>

              <!-- Info Details -->
              <div class="flex-grow-1">
                <div class="h-stack">
                  <div class="info-group">
                    <label class="d-block text-muted">{{
                      $t('field.name')
                    }}</label>
                    <span class="font-weight-medium">{{
                      visitorMetaDataComputed.name || '-'
                    }}</span>
                  </div>
                  <div class="info-group">
                    <label class="d-block text-muted">{{
                      $t('field.enterprise')
                    }}</label>
                    <span class="font-weight-medium">{{
                      getTranslatedName(formData.enterprise)
                    }}</span>
                  </div>
                </div>
                <div class="h-stack mt-2">
                  <div class="info-group">
                    <label class="d-block text-muted">{{
                      $t('general.building_type')
                    }}</label>
                    <span class="font-weight-medium">{{
                      visitorMetaDataComputed.buildingType || '-'
                    }}</span>
                  </div>
                  <div class="info-group">
                    <label class="d-block text-muted">{{
                      $t('general.building_name')
                    }}</label>
                    <span class="font-weight-medium">{{
                      getTranslatedBuildingName()
                    }}</span>
                  </div>
                </div>
                <div class="h-stack mt-2">
                  <div class="info-group">
                    <label class="d-block text-muted">{{
                      $t('field.room')
                    }}</label>
                    <span class="font-weight-medium">{{
                      getTranslatedRoomName()
                    }}</span>
                  </div>
                  <div class="info-group">
                    <label class="d-block text-muted">{{
                      $t('field.project')
                    }}</label>
                    <span class="font-weight-medium">{{
                      getTranslatedName(formData.project)
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Service Details Section -->
        <div class="service-details mb-2">
          <h6 class="mb-1">
            {{ $t('service.details') }}
          </h6>
          <div class="details-card border rounded p-2">

            <div class="service-type-grid">
              <!-- Category Selection -->
              <validation-provider
                v-slot="{ errors }"
                :name="$t('field.category')"
                rules="required"
              >
                <b-form-group
                  :label="$t('field.category')"
                  :state="errors.length === 0 ? null : false"
                  class="mb-2"
                >
                  <n-async-single-select
                    v-model="formData.category"
                    repository="serviceCategory"
                    selection-key="code"
                    selection-label="translations"
                    :reduce="true"
                    :must-excluded-ids="[]"
                    :state="errors.length === 0 ? null : false"
                    :placeholder="$t('placeholder.selectCategory')"
                    :is-object="true"
                    :immediate="false"
                    :readonly="!formData.employee"
                    @change="handleCategoryChange"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <!-- Type Selection -->
            </div>
            <div class="service-type-grid">
              <validation-provider
                v-slot="{ errors, touched }"
                :name="$t('field.type')"
                rules="required"
              >
                <b-form-group
                  :label="$t('field.type')"
                  class="mb-2"
                >
                  <n-async-single-select
                    v-model="formData.type"
                    repository="serviceType"
                    selection-key="code"
                    selection-label="translations"
                    :readonly="!formData.category"
                    :query="{
                      categoryCode: formData.category
                        ? formData.category.code
                        : null,
                      projectId: formData.project
                        ? formData.project.id
                        : null,
                    }"
                    :immediate="false"
                    :reduce="true"
                    :placeholder="$t('placeholder.selectType')"
                    :is-object="true"
                    @change="handleTypeChange"
                  />
                </b-form-group>
              </validation-provider>

              <validation-provider
                v-slot="{ errors }"
                :name="$t('field.subType')"
              >
                <b-form-group
                  :label="$t('field.subType')"
                  :state="errors.length === 0 ? null : false"
                  class="mb-2"
                >
                  <n-async-multi-select
                    :key="resetSubIndex"
                    v-model="formData.subTypes"
                    repository="serviceSubType"
                    selection-key="code"
                    selection-label="translations"
                    clearable
                    :placeholder="$t('placeholder.selectSubType')"
                    :state="errors.length > 0 ? 'is-invalid' : null"
                    :readonly="!formData.type"
                    :is-object="true"
                    always-fresh
                    :immediate="false"
                    :query="{
                      serviceTypeCodes: formData.type
                        ? formData.type.code
                        : null,
                      serviceCategoryCodes: formData.category
                        ? formData.category.code
                        : null,
                    }"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </div>

            <!-- Dynamic Fields Based on Service Type -->
            <div
              v-if="isDynamicFields"
              class="dynamic-fields pt-2 mt-2 border-top"
            >
              <water-delivery-form
                v-if="formData.type.code === 'WATER_DELIVERY_ORDER'"
                v-model="formData.dynamicFields"
                :full-address-list="fullAddressList"
                class="form-radio-service"
                :allow-clipboard="true"
                :project-id="projectId"
                :service-price="servicePriceComputed"
              />
              <cleaning-form
                v-else-if="formData.type.code === 'CLEANING_ORDER'"
                v-model="formData.dynamicFields"
                :full-address-list="fullAddressList"
                :has-active-subscription="hasActiveSubscription"
                class="form-radio-service"
                :allow-clipboard="true"
                :service-price="servicePriceComputed"
              />
              <laundry-form
                v-else-if="formData.type.code === 'LAUNDRY_ORDER'"
                v-model="formData.dynamicFields"
                :chat-channel-id="chatChannelId"
                :full-address-list="fullAddressList"
                class="form-radio-service"
                :allow-clipboard="true"
                :service-price="servicePriceComputed"
              />
              <maintenance-form
                v-else-if="formData.category.code === 'MAINTENANCE'"
                v-model="formData.dynamicFields"
                :chat-channel-id="chatChannelId"
                :full-address-list="fullAddressList"
                class="form-radio-service"
                :allow-clipboard="true"
                :service-price="servicePriceComputed"
              />
            </div>
          </div>
        </div>

        <b-row class="mt-3">
          <b-col class="text-right">
            <n-button-loading
              variant="primary"
              :loading="submitting"
              :disabled="invalid || submitting || !isAddressFilled || !hasSelectedWaterItems || !hasActiveSubscription"
              @submit="onSubmit"
            >
              {{ $t('button.save') }}
            </n-button-loading>
          </b-col>
        </b-row>
      </validation-observer>
    </b-modal>
  </div>
</template>

<script>
import {
  BModal,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BFormSelect,
  BButton,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import NAsyncSingleSelect from '@/components/NAsyncSingleSelect.vue'
import NAsyncMultiSelect from '@/components/NAsyncMultiSelect'
import RepositoryFactory from '@/repositories/RepositoryFactory'
import NButtonLoading from '@/components/NButtonLoading.vue'
import WaterDeliveryForm from '@/components/WateDeliveryFormService.vue'
import CleaningForm from '@/components/CleaningFormService.vue'
import LaundryForm from '@/components/LaundryFormService.vue'
import MaintenanceForm from '@/components/MaintenanceFormService.vue'
import ToastificationAvatar from '@/@core/components/toastification/ToastificationAvatar.vue'
import { formatNumber } from '@/@core/utils/utils'

const ServiceSystemRepository = RepositoryFactory.get('serviceSystem')
const ThirdPartyRepository = RepositoryFactory.get('thirdParty')

export default {
  name: 'ServiceSystemForm',
  components: {
    BModal,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BFormSelect,
    BButton,
    ValidationProvider,
    ValidationObserver,
    NAsyncSingleSelect,
    NAsyncMultiSelect,
    NButtonLoading,
    WaterDeliveryForm,
    CleaningForm,
    LaundryForm,
    MaintenanceForm,
  },
  props: {
    visitorMetaData: {
      type: Object,
      required: true,
    },
    chatChannelId: {
      type: String,
      required: false,
    },
    isButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formatNumber,
      submitting: false,
      locales: [
        { value: 'en', text: 'English' },
        { value: 'zh', text: '中文' },
        { value: 'km', text: 'ខ្មែរ' },
        { value: 'vi', text: 'Tiếng Anh' },
      ],
      formData: {
        employee: null,
        projectId: null,
        project: null,
        enterprise: null,
        category: null,
        type: null,
        subTypes: [],
        dynamicFields: {},
      },
      categoryOptions: [],
      typeOptions: [],
      subTypeOptions: [],
      locale: this.$i18n.locale.split('-')[0],
      resetSubIndex: 0,
      employeeList: [],
      projectManuallySelected: false,
      projectList: [],
      servicePrice: null,
      priceLoading: false,
      priceError: null,
      waterItemsDebounceTimer: null,
      suppressPriceRecalc: false,
      hasActiveSubscription: true,
      buildingTypeObj: {
        dormitoryAddress: {
          en: "Dormitory",
          zh: "宿舍",
          km: "អន្តេវាសិកដ្ឋាន",
          vi: "Ký túc xá",
        },
        apartmentAddress: {
          en: "Apartment",
          zh: "公寓",
          km: "ផ្ទះល្វែង",
          vi: "Căn hộ",
        },
        hotelAddress: {
          en: "Hotel",
          zh: "酒店",
          km: "សណ្ឋាគារ",
          vi: "Khách sạn",
        },
        officeAddress: {
          en: "Office",
          zh: "办公室",
          km: "ការិយាល័យ",
          vi: "Văn phòng",
        },
        storeAddress: {
          en: "Store",
          zh: "商店",
          km: "ហាង",
          vi: "Cửa hàng",
        },
        villaAddress: {
          en: "Villa",
          zh: "别墅",
          km: "វីឡា",
          vi: "Biệt thự",
        },
      },
      resetEnterpriseIndex: 0,
    }
  },
  computed: {
    projectId() {
      return this.formData.project?.id || this.formData.project?.projectId || this.formData.projectId
    },
    priceTypeOptions() {
      return [
        { value: 1, text: this.$t('field.fixed') },
        { value: 2, text: this.$t('field.range') },
        { value: 3, text: this.$t('field.negotiate') },
      ]
    },
    // Dynamic Fields Configuration
    dynamicFields() {
      const typeConfigs = {}
      return typeConfigs[this.formData.type?.code] || []
    },
    isDynamicFields() {
      return (
        this.formData.type
        && this.formData.type?.code
        && (this.formData.type?.code === 'WATER_DELIVERY_ORDER'
          || this.formData.type?.code === 'CLEANING_ORDER'
          || this.formData.type?.code === 'LAUNDRY_ORDER'
          || ((typeof this.formData.category === 'object' ? this.formData.category?.code : this.formData.category) === 'MAINTENANCE'))
      )
    },
    servicePriceComputed() {
      return this.servicePrice && typeof this.servicePrice === 'object'
        ? this.servicePrice
        : null
    },
    servicePriceUnitSuffix() {
      const type = this.servicePriceComputed?.subscriptionType
      if (type === 1) return ` / ${this.$t('general.time')}`
      if (type === 2) return ` / ${this.$t('general.month')}`
      if (type === 3) return ` / ${this.$t('general.unit')}`
      return ''
    },
    isServiceOrder() {
      return (typeof this.formData.category === 'object' ? this.formData.category?.code : this.formData.category) === 'SERVICE_ORDER'
    },
    hasSelectedWaterItems() {
      // Only enforce selection when type is WATER_DELIVERY_ORDER
      if (this.formData?.type?.code !== 'WATER_DELIVERY_ORDER') return true
      const items = this.formData?.dynamicFields?.items
      return Array.isArray(items) && items.length > 0
    },
    isAddressFilled() {
      if (!this.isDynamicFields) return true
      const address = this.formData?.dynamicFields?.address
      if (!address) return false
      if (typeof address === 'string') return address.trim().length > 0
      if (typeof address === 'object') {
        const isCustom = address.isCustom || address.typeKey === 'other'
        if (isCustom) {
          const value = (address.customAddress || address.addressName || '').toString()
          return value.trim().length > 0
        }
        return !!(address.addressName || address.id || address.value)
      }
      return false
    },
    fullAddressList() {
      const selectedId = this.formData.employee
      const emp = this.employeeList.find(e => (e?.id || e?.employee?.id) === selectedId) || null

      if (!emp || !emp.address) return []

      const addressSources = [
        { key: 'dormitoryAddress', label: this.$t('general.dormitory') },
        { key: 'apartmentAddress', label: this.$t('general.apartment') },
        { key: 'hotelAddress', label: this.$t('general.hotel') },
        { key: 'officeAddress', label: this.$t('general.office') },
        { key: 'storeAddress', label: this.$t('general.store') },
        { key: 'villaAddress', label: this.$t('general.villa') },
      ]

      const flattened = []
      for (const src of addressSources) {
        const list = emp.address[src.key]
        if (Array.isArray(list) && list.length > 0) {
          for (const item of list) {
            flattened.push({
              ...item,
              typeKey: src.key,
              typeLabel: src.label,
            })
          }
        }
      }

      return flattened
    },
    visitorMetaDataComputed() {
      const selectedId = this.formData.employee
      const emp = this.employeeList.find(e => (e?.id || e?.employee?.id) === selectedId) || null


      if (!emp) {
        return this.visitorMetaData
      }

      const getValue = (obj, key) => (obj ? obj[key] : undefined)

      const employee = emp.employee || emp
      const enterprise = emp.enterprise || {}
      const addresses = emp.address || {}

      // Prefer address selected in dynamic form; do not fallback if none selected
      const selectedDynamicAddress = this.formData?.dynamicFields?.address || null
      let chosenAddress = selectedDynamicAddress
      let buildingType = selectedDynamicAddress?.typeLabel

      const profileUrl = getValue(employee, 'avatarUrl')
      // Resolve address fields, handling custom address case from child forms
      const fullAddress = chosenAddress
        ? (chosenAddress.isCustom ? (chosenAddress.customAddress || chosenAddress.addressName) : chosenAddress.addressName)
        : undefined
      const roomName = chosenAddress?.spaceName || chosenAddress?.roomName
      const buildingName = chosenAddress?.propertyName || chosenAddress?.buildingName
      const merchantOID = getValue(emp, 'merchantOID')
      const name = getValue(employee, 'name') || emp.name
      const enterpriseName = enterprise?.name || getValue(enterprise, 'name')
      const employeeOID = employee?.id || emp.id

      // Create translation object for meta
      const createTranslation = () => {
        const translation = {}
        // buildingName from propertyName translation
        if (chosenAddress?.translation?.propertyName) {
          translation.buildingName = {
            en: chosenAddress.translation.propertyName.en || chosenAddress.translation.propertyName.zh || chosenAddress.translation.propertyName.km || chosenAddress.translation.propertyName.vi || buildingName || '',
            zh: chosenAddress.translation.propertyName.zh || chosenAddress.translation.propertyName.en || buildingName || '',
            km: chosenAddress.translation.propertyName.km || chosenAddress.translation.propertyName.en || buildingName || '',
            vi: chosenAddress.translation.propertyName.vi || chosenAddress.translation.propertyName.en || buildingName || '',
          }
        } else {
          translation.buildingName = {
            en: buildingName || '',
            zh: buildingName || '',
            km: buildingName || '',
            vi: buildingName || '',
          }
        }
        
        // roomName from spaceName + unitTypeName
        if (chosenAddress?.translation?.spaceName) {
          const spaceNameEn = chosenAddress.translation.spaceName.en || chosenAddress?.spaceName || ''
          const spaceNameZh = chosenAddress.translation.spaceName.zh || chosenAddress?.spaceName || ''
          const spaceNameKm = chosenAddress.translation.spaceName.km || chosenAddress?.spaceName || ''
          const spaceNameVi = chosenAddress.translation.spaceName.vi || chosenAddress?.spaceName || ''
          
          const unitTypeNameEn = chosenAddress?.translation?.unitTypeName?.en || chosenAddress?.unitTypeName || ''
          const unitTypeNameZh = chosenAddress?.translation?.unitTypeName?.zh || chosenAddress?.unitTypeName || ''
          const unitTypeNameKm = chosenAddress?.translation?.unitTypeName?.km || chosenAddress?.unitTypeName || ''
          const unitTypeNameVi = chosenAddress?.translation?.unitTypeName?.vi || chosenAddress?.unitTypeName || ''
          
          translation.roomName = {
            en: unitTypeNameEn ? `${spaceNameEn}, ${unitTypeNameEn}` : spaceNameEn,
            zh: unitTypeNameZh ? `${spaceNameZh}, ${unitTypeNameZh}` : spaceNameZh,
            km: unitTypeNameKm ? `${spaceNameKm}, ${unitTypeNameKm}` : spaceNameKm,
            vi: unitTypeNameVi ? `${spaceNameVi}, ${unitTypeNameVi}` : spaceNameVi,
          }
        } else {
          const spaceName = chosenAddress?.spaceName || ''
          const unitTypeName = chosenAddress?.unitTypeName || ''
          const combinedRoomName = spaceName && unitTypeName ? `${spaceName}, ${unitTypeName}` : (spaceName || unitTypeName || '')
          translation.roomName = {
            en: combinedRoomName,
            zh: combinedRoomName,
            km: combinedRoomName,
            vi: combinedRoomName,
          }
        }
        const ent = this.formData.enterprise?.translationsObj || enterprise?.translationsObj
        // enterpriseName from enterprise translations
        if (ent) {
          translation.enterpriseName = {
            en: ent.en || ent.zh || ent.km || ent.vi || enterpriseName || '',
            zh: ent.zh || ent.en || enterpriseName || '',
            km: ent.km || ent.en || enterpriseName || '',
            vi: ent.vi || ent.en || enterpriseName || '',
          }
        } else {
          translation.enterpriseName = {
            en: enterpriseName || '',
            zh: enterpriseName || '',
            km: enterpriseName || '',
            vi: enterpriseName || '',
          }
        }

        translation.buildingType = this.buildingTypeObj?.[selectedDynamicAddress?.typeKey] || {
          en: buildingType || '',
          zh: buildingType || '',
          km: buildingType || '',
          vi: buildingType || '',
        }
        
        return translation
      }

      return {
        fullAddress,
        roomName,
        employeeOID,
        name,
        enterpriseName,
        buildingName,
        merchantOID,
        profileUrl,
        buildingType,
        translation: createTranslation(),
      }
    },
  },
  watch: {
    'formData.employee': {
      handler(newVal) {
        // Auto select project based on selected employee
        // Clear selected address in dynamic fields when employee changes
        this.formData.dynamicFields = {
          ...(this.formData.dynamicFields || {}),
          address: null,
        }
        const selectedId = newVal
        if (!selectedId) {
          // If project was selected manually, do not override it
          if (!this.projectManuallySelected) {
            this.formData.project = null
            this.formData.projectId = null
          }
          return
        }

        const emp = this.employeeList.find(e => (e?.id || e?.employee?.id) === selectedId) || null
        const projectRaw = emp?.project || emp?.employee?.project || null

        if (!projectRaw) {
          this.formData.project = null
          this.formData.projectId = null
          return
        }

        const projectId = projectRaw.id || projectRaw.projectId
        let projectName = projectRaw.name
        if (projectName && typeof projectName === 'object') {
          let locale = this.$i18n.locale.split('-')[0]
          if (locale === 'zh-cn') locale = 'zh'
          projectName = projectName[locale] || projectName.en || projectName.zh || projectName.km || projectName.vi || ''
        }

        // Only auto-set project if user hasn't selected project manually
        if (!this.projectManuallySelected) {
          this.formData.project = {
            id: projectId,
            name: projectName,
            projectId: projectRaw.projectId || projectId,
          }
          this.formData.projectId = projectId
        }
      },
    },
    'formData.type': {
      handler() {
        // Reset dynamic fields when type changes
        this.suppressPriceRecalc = true
        this.formData.dynamicFields = {}
        // Initialize default values for the new type
        if (this.dynamicFields.length) {
          this.dynamicFields.forEach(field => {
            this.formData.dynamicFields[field.name] = field.type === 'number' ? null : ''
          })
        }
        this.servicePrice = null
        this.$nextTick(() => {
          this.suppressPriceRecalc = false
          this.triggerPriceRecalculate()
        })
      },
      immediate: true,
    },
    'formData.dynamicFields': {
      handler() {
        if (this.suppressPriceRecalc) return
        this.triggerPriceRecalculate()
      },
      deep: true,
    },
    '$i18n.locale': function (val) {
      const [locale] = val.split('-')
      this.locale = locale
    },
    '$route.query.open': {
      handler() {
        this.openIfNeeded()
      },
    },
    '$route.query.category': {
      handler() {
        this.applyServiceCategoryFromQuery()
      },
    },
  },
  mounted() {
    this.fetchOptions()
    this.openIfNeeded()
    this.applyServiceCategoryFromQuery()
    this.getProductList()
  },
  methods: {
    getTranslatedBuildingName() {
      const t = this.formData?.dynamicFields?.address?.translation
      if (!t) return '-'
      const locale = this.$i18n.locale.split('-')[0]
      return t?.propertyName?.[locale] || t?.propertyName?.en || t?.propertyName?.zh || t?.propertyName?.km || t?.propertyName?.vi || '-'
    },
    getTranslatedRoomName() {
      const t = this.formData?.dynamicFields?.address?.translation
      if (!t) return '-'
      const locale = this.$i18n.locale.split('-')[0]
      return t?.spaceName?.[locale] || t?.spaceName?.en || t?.spaceName?.zh || t?.spaceName?.km || t?.spaceName?.vi || '-'
    },
    handleProjectChange(val) {
      // If project changed manually, keep projectId in sync and clear dependent fields
      this.projectManuallySelected = true
      this.formData.projectId = val?.id || val?.projectId || null
      // Reset enterprise and employee when project changes
      this.formData.enterprise = null
      this.formData.employee = null
      this.employeeList = []
      this.formData.type = null
      this.resetSubIndex += 1
    },
    handleEnterpriseChange() {
      // Clear employee when enterprise changed to force reselect
      this.formData.employee = null
      this.employeeList = []
      this.resetEnterpriseIndex += 1
      // Trigger employee refetch via bound query props (n-async-single-select handles it on open)
    },
    resolveAddressValue(raw) {
      if (!raw) return null
      if (typeof raw === 'string') return raw.trim()
      if (typeof raw === 'object') {
        const isCustom = raw.isCustom || raw.typeKey === 'other'
        const val = isCustom ? (raw.customAddress || raw.addressName || '') : (raw.addressName || raw.name || '')
        return (val || '').toString().trim()
      }
      return null
    },
    resolveAddressObjectValue(raw) {
      // Prefer multilingual translation object from selected address
      const empty = { en: '', zh: '', km: '', vi: '' }
      if (!raw) return empty
      // If nested under `.address`, unwrap
      const addressObj = raw.address || raw
      const translationObj = addressObj?.translation?.addressName
      if (translationObj && typeof translationObj === 'object') {
        return {
          en: (translationObj.en || '').toString(),
          zh: (translationObj.zh || translationObj['zh-cn'] || '').toString(),
          km: (translationObj.km || '').toString(),
          vi: (translationObj.vi || '').toString(),
        }
      }
      // If already multilingual-like
      if (typeof addressObj === 'object' && ['en','km','vi','zh'].some(k => Object.prototype.hasOwnProperty.call(addressObj, k))) {
        return {
          en: (addressObj.en || '').toString(),
          zh: (addressObj.zh || addressObj['zh-cn'] || '').toString(),
          km: (addressObj.km || '').toString(),
          vi: (addressObj.vi || '').toString(),
        }
      }
      // Fallbacks
      if (typeof addressObj === 'string') {
        const v = addressObj.trim()
        return { en: v, zh: v, km: v, vi: v }
      }
      if (typeof addressObj === 'object') {
        const isCustom = addressObj.isCustom || addressObj.typeKey === 'other'
        const v = (isCustom ? (addressObj.customAddress || addressObj.addressName || addressObj.name) : (addressObj.addressName || addressObj.name) || '').toString().trim()
        return { en: v, zh: v, km: v, vi: v }
      }
      return empty
    },
    buildParamsFromDynamicFields() {
      const params = []
      const dynamicFields = this.formData.dynamicFields || {}
      
      // Helper function to create multilingual name object
      const createMultilingualName = (enText, zhText, kmText, viText) => ({
        en: enText,
        zh: zhText || enText,
        km: kmText || enText,
        vi: viText || enText,
      })
      
      // Process each field in dynamicFields
      Object.keys(dynamicFields).forEach(key => {
        const value = dynamicFields[key]
        
        // Skip null, undefined, empty strings, and the address field (handled separately)
        if (value == null || value === '' || key === 'address') return
        
        let paramName = {}
        let paramValue = value
        let paramType = 'text'
        
        // Map field keys to proper names with translations
        switch (key) {
          case 'orderId':
            paramName = createMultilingualName('Order ID', '订单编号', 'លេខសម្គាល់ការបញ្ជាទិញ', 'Mã đơn hàng')
            break
          case 'createdBy':
            paramName = createMultilingualName('Created By', '创建者', 'បង្កើតដោយ', 'Được tạo bởi')
            break
          case 'description':
            paramName = createMultilingualName('Description', '描述', 'ការពិពណ៌នា', 'Sự miêu tả')
            break
          case 'expectedDate':
            paramName = createMultilingualName('Expected Date', '预计日期', 'កាលបរិច្ឆេទរំពឹងទុក', 'Ngày dự kiến')
            // Handle special case for ASAP
            if (value === 'ASAP' || (typeof value === 'object' && value.en === 'ASAP')) {
              paramValue = createMultilingualName('ASAP', '尽快', 'ឱ្យបានឆាប់', 'càng sớm càng tốt')
            }
            break
          case 'files':
            paramName = createMultilingualName('Files', '文件', 'ឯកសារ', 'Tập tin')
            paramType = 'file'
            paramValue = ''
            break
          case 'quantity':
            paramName = createMultilingualName('Quantity', '数量', 'បរិមាណ', 'Số lượng')
            break
          case 'price':
            paramName = createMultilingualName('Price', '价格', 'តម្លៃ', 'Giá cả')
            break
          case 'notes':
            paramName = createMultilingualName('Notes', '备注', 'កំណត់ចំណាំ', 'Ghi chú')
            break
          case 'priority':
            paramName = createMultilingualName('Priority', '优先级', 'អាទិភាព', 'Ưu tiên')
            break
          case 'serviceDate':
            paramName = createMultilingualName('Service Date', '服务日期', 'កាលបរិច្ឆេទសេវាកម្ម', 'Ngày dịch vụ')
            break
          case 'serviceTime':
            paramName = createMultilingualName('Service Time', '服务时间', 'ពេលវេលាសេវាកម្ម', 'Thời gian dịch vụ')
            break
          case 'specialInstructions':
            paramName = createMultilingualName('Special Instructions', '特殊说明', 'ការណែនាំពិសេស', 'Hướng dẫn đặc biệt')
            break
          case 'scheduleType':
            paramName = createMultilingualName('Schedule Type', '计划类型', 'ប្រភេទកាលវិភាគ', 'Loại lịch trình')
            break
          case 'scheduleDate':
            paramName = createMultilingualName('Schedule Date', '计划日期', 'កាលបរិច្ឆេទកាលវិភាគ', 'Ngày lịch trình')
            break
          case 'chargeType':
            paramName = createMultilingualName('Charge Type', '收费类型', 'ប្រភេទការគិតថ្លៃ', 'Loại phí')
            break
          case 'items':
            paramName = createMultilingualName('Order Items', '订单', 'ការបញ្ជាទិញ', 'Đơn hàng')
            break
          default:
            // For unknown fields, use the key as the English name and create basic translations
            const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')
            paramName = createMultilingualName(capitalizedKey, capitalizedKey, capitalizedKey, capitalizedKey)
        }
        
        const param = {
          type: paramType,
          name: paramName,
          key,
          value: paramValue,
        }
        
        // Add files array for file type
        if (paramType === 'file' && Array.isArray(value)) {
          param.files = value.map(file => ({
            mimeType: file.mimeType || 'application/octet-stream',
            url: file.url || file.src || file.path || '',
          }))
        }
        
        params.push(param)
      })
      
      // Add address as a separate parameter if it exists
      const addressValue = this.resolveAddressObjectValue(dynamicFields.address)
      if (addressValue) {
        params.push({
          type: 'text',
          name: createMultilingualName('Address', '地址', 'អាស័យដ្ឋាន', 'Địa chỉ'),
          key: 'address',
          value: addressValue,
        })
      }
      
      return params
    },
    getProductList() {
      ThirdPartyRepository.productList({
        page: 1,
        limit: 100,
        projectOID: this.projectId,
      }).then(response => {
        console.log(response)
      })
    },
    openIfNeeded() {
      const flag = (this.$route?.query?.open || '').toString().toLowerCase()
      if (flag === '1' || flag === 'true') {
        this.$nextTick(() => this.show())
      }
    },
    applyServiceCategoryFromQuery() {
      const category = (this.$route?.query?.category || '').toString().toUpperCase()
      if (!category) return
      if (category === 'SERVICE_ORDER' || category === 'MAINTENANCE') {
        this.formData.category = category
        this.formData.type = null
        this.resetSubIndex += 1
      }
    },
    handleTypeChange() {
      this.resetSubIndex += 1
    },
    triggerPriceRecalculate() {
      const category = typeof this.formData.category === 'object' ? this.formData.category?.code : this.formData.category
      if (!(category === 'SERVICE_ORDER' || category === 'MAINTENANCE')) return
      if (this.formData.type?.code === 'WATER_DELIVERY_ORDER') {
        if (this.waterItemsDebounceTimer) clearTimeout(this.waterItemsDebounceTimer)
        this.waterItemsDebounceTimer = setTimeout(() => this.calculateServicePrice(), 400)
      } else {
        this.calculateServicePrice()
      }
    },
    buildPricePayload() {
      const category = typeof this.formData.category === 'object' ? this.formData.category?.code : this.formData.category
      const type = this.formData.type?.code
      const payload = {
        employeeOID: (typeof this.formData.employee === 'object' ? this.formData.employee?.id : this.formData.employee),
      }

      if (this.formData?.dynamicFields?.address?.spaceOID) {
        payload.spaceOID = this.formData?.dynamicFields?.address?.spaceOID
      }
      
      if (type === 'WATER_DELIVERY_ORDER') {
        const items = (this.formData.dynamicFields?.items || []).map(it => ({ id: it.id, qty: it.qty }))
        payload.items = items
      }
      return { category, type, payload }
    },
    async calculateServicePrice() {
      const { category, type, payload } = this.buildPricePayload()
      if (!type) return
      // Prevent empty payloads for these types
      if ((type === 'LAUNDRY_ORDER' || type === 'CLEANING_ORDER' || category === 'MAINTENANCE') && !payload.employeeOID) return
      this.priceLoading = true
      this.priceError = null
      try {
        let response
        if (type === 'WATER_DELIVERY_ORDER') {
          response = await ServiceSystemRepository.priceValidateWaterDelivery(payload)
        } else if (type === 'CLEANING_ORDER') {
          response = await ServiceSystemRepository.priceValidateCleaning(payload)
        } else if (type === 'LAUNDRY_ORDER') {
          response = await ServiceSystemRepository.priceValidateLaundry(payload)
        } else if (category === 'MAINTENANCE') {
          response = await ServiceSystemRepository.priceValidateMaintenance(payload)
        }
        
        if (response && response.data) {
          this.servicePrice = response.data?.data || response.data
          if (type === 'CLEANING_ORDER' && this.formData.dynamicFields.address) {
            this.hasActiveSubscription = this.servicePrice.hasActiveSubscription !== false
          }
        }
      } catch (e) {
        this.priceError = e
        this.servicePrice = null
      } finally {
        this.priceLoading = false
      }
    },
    show() {
      this.$refs.modal.show()
    },
    hide() {
      this.$refs.modal.hide()
    },
    onHidden() {
      this.resetForm()
      this.$emit('hide')
    },
    resetForm() {
      this.formData = {
        employee: null,
        projectId: null,
        project: null,
        enterprise: null,
        category: null,
        type: null,
        subTypes: [],
        dynamicFields: {},
      }
      this.hasActiveSubscription = true
      this.servicePrice = null
      if (this.$refs.formValidation) {
        this.$refs.formValidation.reset()
      }
    },
    async fetchOptions() {
      try {
        const [categoryResponse, typeResponse] = await Promise.all([
          ServiceSystemRepository.getServiceCategory(),
          ServiceSystemRepository.getServiceType(),
        ])

        if (categoryResponse.data.data.list) {
          this.categoryOptions = categoryResponse.data.data.list.map(
            category => ({
              value: category.value || category.id,
              text: category.text || category.name,
            }),
          )
        }

        if (typeResponse.data.data.list) {
          this.typeOptions = typeResponse.data.data.list.map(type => ({
            value: type.value || type.id,
            text: type.text || type.name,
          }))
        }
      } catch (error) {
        console.error('Error fetching options:', error)
      }
    },
    async onSubmit() {
      try {
        const isValid = await this.$refs.formValidation.validate()
        if (!isValid || this.submitting) return

        this.submitting = true

        const selectedProject = this.projectList.find(project => project.id === this.formData.project?.id)
        
        const categoryCode = (typeof this.formData.category === 'object' ? this.formData.category?.code : this.formData.category)
        const typeCode = this.formData.type?.code
        const isPricedType = typeCode === 'WATER_DELIVERY_ORDER' || typeCode === 'CLEANING_ORDER' || typeCode === 'LAUNDRY_ORDER'

        const payload = {
          chatChannelId: this.chatChannelId,
          projectId: this.formData.project?.id || this.formData.project?.projectId || this.formData.projectId,
          projectName: selectedProject && Object.keys(selectedProject.translationsObj)?.length ? selectedProject.translationsObj : this.formData.project?.name,
          serviceCategory: {
            id: this.formData.category?.id,
            name: this.formData.category?.name,
            code: this.formData.category?.code,
          },
          serviceType: {
            name: this.formData.type?.name,
            code: this.formData.type?.code,
          },
          serviceSubTypes: this.formData.subTypes?.map(subType => ({
            id: subType?.id || undefined,
            name: subType?.name || undefined,
            code: subType?.code || undefined,
          })),
          finalResultParams: {
            ...this.formData.dynamicFields,
            address: this.resolveAddressValue(this.formData.dynamicFields.address),
            ...(this.formData.dynamicFields.address ? {
              addressInfo: {
                propertyOID: this.formData.dynamicFields.address.propertyOID,
                floorOID: this.formData.dynamicFields.address.floorOID,
                spaceOID: this.formData.dynamicFields.address.spaceOID,
              }
            } : {}),
          },
          params: this.buildParamsFromDynamicFields(),
          meta: {
            ...this.visitorMetaDataComputed,
          },
          // Service Price fields
          servicePriceCurrency: 'USD',
          ...(categoryCode === 'SERVICE_ORDER' && isPricedType ? { servicePriceType: 1 } : {}),
          ...(this.servicePriceComputed && this.servicePriceComputed.total != null ? { servicePriceInit: this.servicePriceComputed.total } : {}),
          allowResetPrice: !!this.formData?.type?.allowResetPrice
        }

        await ServiceSystemRepository.createServicePanel(payload)
        this.$toast({
          component: ToastificationAvatar,
          position: 'top-right',
          timeout: 10000,
          props: {
            title: this.$t('alert.service'),
            icon: 'CastIcon',
            variant: 'success',
            text: this.$t('service.create_success'),
          },
        })
        this.hide()
        this.resetForm()
        this.$emit('submit')
      } catch (error) {
        console.error('Error creating service:', error)
      } finally {
        this.submitting = false
      }
    },
    handleCategoryChange() {
      this.formData.type = null
      this.resetSubIndex += 1
    },
    getTranslatedName(name) {
      if (!name) return '-'
      if (typeof name === 'string') return name
      if (typeof name === 'object') {
        const locale = this.$i18n.locale.split('-')[0]
        return name?.translationsObj?.[locale] || name?.translationsObj?.en || name?.translationsObj?.zh || name?.translationsObj?.km || name?.translationsObj?.vi || '-'
      }
      return '-'
    },
  },
}
</script>

<style lang="scss" scoped>
  .h-stack {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1em;
  }

  .service-type-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
  }

  .language-select {
    height: 100%;
  }

  .disabled-icon {
    cursor: not-allowed;
    opacity: 0.3;
  }

  .clickable-icon {
    cursor: pointer;
  }

  .service-details,
  .price-configuration,
  .basic-info {
    .details-card,
    .price-details,
    .info-card.info-card {
      border: 1px solid #dee2e6;
      border-radius: 0.25rem;
      gap: 2rem;
      align-items: start;
    }
  }

  .profile-image-container {
    width: 80px;
    height: 80px;
    flex-shrink: 0;
    border-radius: 50%;
    overflow: hidden;
    border: 2px solid #dee2e6;
    background-color: #f8f9fa;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .profile-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .info-group {
    label {
      margin-bottom: 0.25rem;
    }

    span {
      font-size: 0.95rem;
    }
  }

  /* 30% / 70% split inside basic info card */
  .basic-info .info-card {
    display: grid !important;
    grid-template-columns: 40% 60%;
    align-items: center;
    gap: 0.5rem;
  }

  .basic-info .employee-search {
    width: 100%;
  }

  .basic-info .details-right {
    width: 100%;
  }

  .btn-add {
    color: white !important;
    margin-left: 0.5rem;
  }
</style>

<style>
.form-radio-service .custom-radio.custom-control  {
  margin-bottom: 6px;
}
.spinner-border {
  height: 20px;
  width: 20px;
}
</style>